import { config } from '@/config/env';
import type { GiftTerm } from '@shared/types';
import { initializePaddle, type Paddle, type PaddleEventData } from '@paddle/paddle-js';

let paddlePromise: Promise<Paddle | undefined> | null = null;
let completedListener: ((data: PaddleEventData) => void) | null = null;

export function getPaddle(): Promise<Paddle | undefined> {
  if (!paddlePromise) {
    paddlePromise = initializePaddle({
      token: config.paddle.clientToken,
      environment: config.paddle.environment,
      eventCallback: (event) => {
        if (event.name === 'checkout.completed' && completedListener) completedListener(event);
      },
    });
  }
  return paddlePromise;
}

export interface LocalizedPrice {
  term: GiftTerm;
  formattedTotal: string;
  currencyCode: string;
}

/** Localized prices for both gift terms, via Paddle's PricePreview (tax inclusive where applicable). */
export async function previewGiftPrices(): Promise<LocalizedPrice[]> {
  const paddle = await getPaddle();
  if (!paddle) return [];
  const terms: GiftTerm[] = ['3m', '6m'];
  const items = terms
    .filter((t) => config.paddle.priceIds[t])
    .map((t) => ({ priceId: config.paddle.priceIds[t], quantity: 1 }));
  if (items.length === 0) return [];
  const preview = await paddle.PricePreview({ items });
  return preview.data.details.lineItems.map((li) => {
    const term = (terms.find((t) => config.paddle.priceIds[t] === li.price.id) ?? '3m') as GiftTerm;
    return { term, formattedTotal: li.formattedTotals.total, currencyCode: preview.data.currencyCode };
  });
}

export interface OpenGiftCheckoutInput {
  term: GiftTerm;
  giftId: string;
  manageToken: string;
  gifterEmail: string;
  onCompleted: () => void;
}

export async function openGiftCheckout(input: OpenGiftCheckoutInput): Promise<void> {
  const paddle = await getPaddle();
  if (!paddle) throw new Error('Payments are unavailable right now');
  completedListener = () => input.onCompleted();
  const successUrl = `${window.location.origin}/thanks?g=${encodeURIComponent(input.giftId)}&t=${encodeURIComponent(
    input.manageToken
  )}`;
  paddle.Checkout.open({
    items: [{ priceId: config.paddle.priceIds[input.term], quantity: 1 }],
    // The GIFTER is the Paddle customer: Paddle emails the receipt to them, never to the giftee.
    customer: { email: input.gifterEmail },
    customData: { kind: 'gift', giftId: input.giftId },
    settings: {
      displayMode: 'overlay',
      variant: 'one-page',
      theme: 'light',
      allowLogout: false,
      showAddDiscounts: false,
      successUrl,
    },
  });
}
